import React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/Input";
import { Card, CardContent } from "@/components/ui/Card"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/Table"
import { getSectionRiskColor } from "@/services/sectionRiskService";
import { getControlEfficacyColor } from "@/services/controlEfficacyService";

const generateRowId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID()
    }
    return `section-row-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const createEmptySectionRow = (overrides = {}) => ({
    id: overrides.id ?? generateRowId(),
    persistedId: overrides.persistedId ?? null,
    risk: overrides.risk ?? "",
    category: overrides.category ?? "",
    inherentRiskScore: overrides.inherentRiskScore ?? "",
    inherentRiskLabel: overrides.inherentRiskLabel ?? "",
    controlEfficacy: overrides.controlEfficacy ?? "",
    riskScore: overrides.riskScore ?? "",
})

const SectionRiskScoreTab = ({ rows, onRiskCategoryChange = () => { } }) => {
    const sectionRows = rows || [];

    const sectionColumns = [
        { key: "risk", label: "Risk Section", minWidth: 220 },
        { key: "category", label: "Risk Sub-Section", minWidth: 240 },
        { key: "inherentRisk", label: "Inherent Risk", minWidth: 200 },
        { key: "controlEfficacy", label: "Control Efficacy", minWidth: 200 },
        { key: "riskScore", label: "Sub-Section Risk Score", minWidth: 200 },
    ]

    const createHeadClassNames = (stickyKey) => (key) =>
        cn(
            "p-4 text-xs font-semibold tracking-wide uppercase text-gray-200 whitespace-nowrap bg-transparent",
            key === stickyKey && "sticky left-0 z-30 bg-navy-600"
        )

    const createCellClassNames = (stickyKey) => (key) =>
        cn(
            "p-3 text-sm text-gray-0 last:border-b-0",
            key === stickyKey && "sticky left-0 z-10 bg-navy-600"
        )

    const headClassNames = createHeadClassNames(sectionColumns[0]?.key)
    const cellClassNames = createCellClassNames(sectionColumns[0]?.key)

    const columnStyle = (column) => ({
        minWidth: column.minWidth,
    })

    const renderCellContent = (column, row) => {
        // Risk and Category are now read-only
        if (column.key === "risk" || column.key === "category") {
            return (
                <div className="flex items-center h-12 px-3 rounded-t-lg bg-navy-100 text-gray-0">
                    {row[column.key] || <span className="text-gray-400">-</span>}
                </div>
            )
        }

        if (column.key === "inherentRisk") {
            const hasValues = row.inherentRiskLabel || row.inherentRiskScore
            return (
                <div className="flex items-center h-12 px-3 rounded-t-lg bg-navy-100 text-gray-0">
                    {hasValues ? (
                        <div className="flex flex-col leading-tight">
                            <span className="text-sm text-gray-0">
                                {row.inherentRiskScore}
                            </span>
                        </div>
                    ) : (
                        <span className="text-gray-400">-</span>
                    )}
                </div>
            )
        }

        if (column.key === "controlEfficacy") {
            return (
                <div className="flex items-center h-12 px-3 rounded-t-lg bg-navy-100 text-gray-0">
                    {row.controlEfficacy ? (
                        <div className="flex items-center gap-2">
                            <span className={cn("w-3 h-3 rounded-full", getControlEfficacyColor(row.controlEfficacy))} />
                            <span className="text-sm text-gray-0">{row.controlEfficacy}</span>
                        </div>
                    ) : (
                        <span className="text-gray-400">-</span>
                    )}
                </div>
            )
        }

        if (column.key === "riskScore") {
            const hasScore = row.riskScore && row.controlEfficacy
            const inherentNum = parseInt(row.inherentRiskScore, 10);
            const hasValidInherent = row.inherentRiskScore && !Number.isNaN(inherentNum) && inherentNum >= 1 && inherentNum <= 10;
            return (
                <div className="flex items-center h-12 px-3 rounded-t-lg bg-navy-100 text-gray-0">
                    {hasScore ? (
                        <div className="flex items-center gap-2">
                            <span className={cn("w-3 h-3 rounded-full", getSectionRiskColor(row.riskScore, hasValidInherent))} />
                            <span className="text-sm text-gray-0">{row.riskScore}</span>
                        </div>
                    ) : (
                        <span className="text-gray-400">-</span>
                    )}
                </div>
            )
        }

        return null
    }

    return (
        <Card className="pt-4 border-none slide-up">
            <CardContent className="p-0">
                <div className="space-y-4">
                    <div className="relative custom-scrollbar">
                        {sectionRows.length === 0 ? (
                            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-gray-400 border border-gray-600 border-dashed rounded-lg">
                                <p className="text-base font-semibold text-gray-0">No section risk score rows yet</p>
                                <p className="text-sm text-gray-400">First, add a risk and category to the mine type. <a href="/mine-types" className="text-red-500 capitalize">Click here</a> to add a risk and category.</p>
                            </div>
                        ) : (
                            <>
                                <div className="sticky top-[48px] z-20 bg-navy-600">
                                    <Table className="table-fixed w-full" containerClassName="overflow-visible">
                                        <colgroup>
                                            {sectionColumns.map((column) => (
                                                <col key={column.key} style={{ width: column.minWidth }} />
                                            ))}
                                        </colgroup>
                                        <TableHeader className="text-left">
                                            <TableRow>
                                                {sectionColumns.map((column) => (
                                                    <TableHead
                                                        key={column.key}
                                                        className={headClassNames(column.key)}
                                                        style={columnStyle(column)}
                                                    >
                                                        {column.label}
                                                    </TableHead>
                                                ))}
                                            </TableRow>
                                        </TableHeader>
                                    </Table>
                                </div>
                                <Table className="table-fixed w-full" containerClassName="overflow-visible">
                                    <colgroup>
                                        {sectionColumns.map((column) => (
                                            <col key={column.key} style={{ width: column.minWidth }} />
                                        ))}
                                    </colgroup>
                                    <TableBody>
                                        {sectionRows.map((row) => (
                                            <TableRow key={`section-row-${row.id}`}>
                                                {sectionColumns.map((column) => (
                                                    <TableCell
                                                        key={column.key}
                                                        className={cellClassNames(column.key)}
                                                        style={columnStyle(column)}
                                                    >
                                                        {renderCellContent(column, row)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

SectionRiskScoreTab.displayName = "SectionRiskScoreTab"

export default SectionRiskScoreTab;
export { createEmptySectionRow };
